import { Directive, ElementRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { foldToAsciiForXeroTrackingInput, foldToAsciiForXeroTrackingInputTrimmed } from '@gh/core-util';
import { noop } from 'lodash';

@Directive({
  standalone: false,
  selector: 'input[ghXeroTrackingNormalize],textarea[ghXeroTrackingNormalize]',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: XeroTrackingInputNormalizeDirective, multi: true }],
})
export class XeroTrackingInputNormalizeDirective implements ControlValueAccessor {
  private _onChange: (value: string) => void = noop;
  private _onTouched: () => void = noop;

  private lastValue = '';
  private userEditedSinceWrite = false;

  constructor(private elementRef: ElementRef<HTMLInputElement | HTMLTextAreaElement>) {}

  writeValue(value: string | null): void {
    const raw = value ?? '';
    this.updateElementValue(raw);
    this.lastValue = raw;
    this.userEditedSinceWrite = false;
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    this.userEditedSinceWrite = true;
    const el = event.target as HTMLInputElement | HTMLTextAreaElement;
    const oldVal = el.value;
    const newVal = foldToAsciiForXeroTrackingInput(oldVal);

    const start = el.selectionStart ?? oldVal.length;
    const end = el.selectionEnd ?? oldVal.length;
    const caretAtEnd = start === oldVal.length && end === oldVal.length;

    this.updateElementValue(newVal);

    if (caretAtEnd) {
      el.setSelectionRange(newVal.length, newVal.length);
    } else {
      const delta = newVal.length - oldVal.length;
      const nextStart = Math.max(0, Math.min(newVal.length, start + delta));
      const nextEnd = Math.max(0, Math.min(newVal.length, end + delta));
      el.setSelectionRange(nextStart, nextEnd);
    }

    if (this.lastValue !== newVal) {
      this.lastValue = newVal;
      this._onChange(newVal);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    const el = this.elementRef.nativeElement;
    if (!this.userEditedSinceWrite) {
      this._onTouched();
      return;
    }
    const newVal = foldToAsciiForXeroTrackingInputTrimmed(el.value);
    if (el.value !== newVal) {
      el.value = newVal;
      this.lastValue = newVal;
      this._onChange(newVal);
    }
    this._onTouched();
    this.userEditedSinceWrite = false;
  }

  private updateElementValue(value: string): void {
    this.elementRef.nativeElement.value = value;
  }
}
